import clsx from "clsx";
import { proxyLocalUploadUrl } from "./drawingPdfUtils";

export const DRAWING_DOCUMENT_FIELDS =
  "id,title,file,pdf_optimized_image_url,drawing_id,drawing_position_x,drawing_position_y,drawing_snapshot_document_id,drawing_snapshot_document,include_drawing_snapshot_in_print";

export const documentImageUrl = (document) =>
  proxyLocalUploadUrl(
    document?.preview_image_url ||
      document?.file?.thumb?.url ||
      document?.file?.thumb?.full_url ||
      document?.file?.url ||
      document?.file?.full_url ||
      document?.pdf_optimized_image_url
  );

/** Full-resolution original — for "open in new tab", never the ~200x200 thumb. */
export const documentFullImageUrl = (document) =>
  proxyLocalUploadUrl(
    document?.file?.full_url ||
      document?.file?.url ||
      document?.preview_image_url ||
      document?.pdf_optimized_image_url
  );

/** Block protocol-relative / javascript: URLs; allow same-origin paths and http(s). */
export const safeDocumentHref = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("//")) return null;
  if (trimmed.startsWith("/")) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return proxyLocalUploadUrl(trimmed);
  return null;
};

const isSvgDocument = (document, url) => {
  const contentType = String(
    document?.file?.content_type || document?.content_type || ""
  ).toLowerCase();
  if (contentType.includes("svg")) return true;
  return /\.svg(\?|#|$)/i.test(String(url || ""));
};

/** Img-only for SVG (no raw navigation XSS); safe <a> for other images. */
const DocumentThumb = ({ document, alt = "" }) => {
  const rawUrl = documentImageUrl(document);
  const thumbHref = safeDocumentHref(rawUrl);
  if (!thumbHref) {
    return (
      <div className="flex h-20 items-center justify-center text-[10px] text-[#94a3b8]">
        —
      </div>
    );
  }
  const img = (
    <img src={thumbHref} alt={alt} className="h-20 w-full object-cover" />
  );
  if (isSvgDocument(document, thumbHref)) {
    return <div className="block">{img}</div>;
  }
  const fullHref =
    safeDocumentHref(documentFullImageUrl(document)) || thumbHref;
  return (
    <a
      href={fullHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="block"
    >
      {img}
    </a>
  );
};

/**
 * Group deviation/checklist documents into pin cards (photo + snapshot).
 * Snapshot rows that are only linked via drawing_snapshot_document_id are excluded.
 */
export const buildDocumentPinGroups = (documents) => {
  // Drop null/undefined slots (e.g. after a failed upload leaves a sparse list).
  const list = (Array.isArray(documents) ? documents : []).filter(
    (d) => d != null && typeof d === "object"
  );
  const snapshotIds = new Set(
    list
      .map(
        (d) =>
          d.drawing_snapshot_document_id || d.drawing_snapshot_document?.id
      )
      .filter(Boolean)
      .map(String)
  );
  return list
    .filter((d) => d.id != null && !snapshotIds.has(String(d.id)))
    .map((doc) => {
      const snapshot =
        doc.drawing_snapshot_document ||
        list.find(
          (d) =>
            d.id != null &&
            String(d.id) === String(doc.drawing_snapshot_document_id)
        ) ||
        null;
      const hasPin =
        doc.drawing_id != null &&
        doc.drawing_position_x != null &&
        doc.drawing_position_y != null;
      return { doc, snapshot, hasPin };
    });
};

/**
 * Pin-group cards (snapshot + photo) — same pattern as Tegninger avviks-dialog.
 * Interactive focus callbacks are optional (used in drawing viewer only).
 */
export const DrawingDocumentPinGroups = ({
  t,
  documents = [],
  emptyLabel = null,
  focusedDocumentId = null,
  onFocusDocumentPin = null,
  className = "",
}) => {
  const groups = buildDocumentPinGroups(documents);

  if (groups.length === 0) {
    return (
      <p className="text-[12px] text-[#59616d]">
        {emptyLabel || t("drawings.observation.deviation_docs_empty")}
      </p>
    );
  }

  return (
    <ul className={clsx("space-y-2", className)}>
      {groups.map(({ doc, snapshot, hasPin }, groupIndex) => {
        const isFocused =
          focusedDocumentId != null &&
          String(focusedDocumentId) === String(doc.id);
        const canFocusPin =
          hasPin && typeof onFocusDocumentPin === "function";

        const cardClass = clsx(
          "w-full rounded-xl border bg-white p-2 text-left transition-shadow touch-manipulation",
          canFocusPin
            ? isFocused
              ? "border-[#e5484d] shadow-[0_0_0_2px_rgba(229,72,77,0.25)]"
              : "border-[#e5e8ec] hover:border-[#f5c2c4] hover:shadow-sm"
            : "cursor-default border-[#e5e8ec]"
        );

        const cardBody = (
          <>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-[#14171c]">
                {t("drawings.viewer.checklist_pin_group", {
                  n: groupIndex + 1,
                })}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  hasPin
                    ? "bg-[#eaf8ef] text-[#0b7a3b]"
                    : "bg-[#f1f5f9] text-[#64748b]"
                }`}
              >
                {hasPin
                  ? t("drawings.viewer.checklist_item_pinned")
                  : t("drawings.viewer.checklist_unpinned_docs")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="overflow-hidden rounded-lg border border-[#e5e8ec] bg-[#f8fafc]">
                <p className="border-b border-[#e5e8ec] px-1.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-[#59616d]">
                  {t("drawings.observation.snapshot_title")}
                </p>
                <DocumentThumb document={snapshot} alt="" />
              </div>
              <div className="overflow-hidden rounded-lg border border-[#e5e8ec] bg-[#f8fafc]">
                <p className="border-b border-[#e5e8ec] px-1.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-[#59616d]">
                  {t("drawings.observation.field.photo")}
                </p>
                {documentImageUrl(doc) ? (
                  <DocumentThumb document={doc} alt={doc.title || ""} />
                ) : (
                  <div className="flex h-20 items-center justify-center px-1 text-center text-[10px] text-[#94a3b8]">
                    {doc.title || "—"}
                  </div>
                )}
              </div>
            </div>
          </>
        );

        return (
          <li key={doc.id}>
            {canFocusPin ? (
              <button
                type="button"
                data-drawing-doc-id={doc.id}
                onClick={() =>
                  onFocusDocumentPin?.(doc, { pinFocus: true })
                }
                onMouseEnter={() => onFocusDocumentPin?.(doc)}
                onFocus={() => onFocusDocumentPin?.(doc)}
                className={cardClass}
              >
                {cardBody}
              </button>
            ) : (
              <div data-drawing-doc-id={doc.id} className={cardClass}>
                {cardBody}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
